import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import { useSelector } from 'react-redux';

import { DuelPlayerSummaryDto, getDuelPlayerSummary } from '../../../services/api';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { Pressable } from '../common/Touchable';
import { duelText } from './duelsText';
import { divisionBadge, formatScore } from './duelsTheme';
import { duelTextStyles } from './DuelsUi';

type Props = {
	playerId: string | null | undefined;
	scale: number;
	style: StyleProp<ViewStyle>;
	onOpen?: () => void;
	arrow?: React.ReactNode;
};

export const DuelProfileRow = ({ playerId, scale, style, onOpen, arrow }: Props) => {
	const { t } = useTranslation();
	const isLogged = useSelector(selectIsLogged);
	const [summary, setSummary] = useState<DuelPlayerSummaryDto | null>(null);

	useEffect(() => {
		setSummary(null);
		if (!isLogged || !playerId) return;
		let active = true;
		getDuelPlayerSummary(String(playerId))
			.then(result => {
				if (active) setSummary(result);
			})
			.catch(() => {
				if (active) setSummary(null);
			});
		return () => {
			active = false;
		};
	}, [isLogged, playerId]);

	if (!summary || summary.duelsPlayed === 0) return null;

	return (
		<View style={[style, styles.row, { columnGap: 10 * scale }]}>
			<Image
				source={divisionBadge(summary.division.name)}
				style={{ width: 40 * scale, height: 40 * scale }}
				resizeMode='contain'
			/>
			<View style={styles.flex}>
				<Text style={[duelTextStyles.gold, { fontSize: 10 * scale }]}>
					{duelText(t, 'T_SITE_DUELS_PROFILE_LABEL')}
				</Text>
				<Text numberOfLines={1} style={[duelTextStyles.title, { fontSize: 15 * scale }]}>
					{summary.division.label.toUpperCase()}
				</Text>
				<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
					{duelText(t, 'T_SITE_DUELS_RATING', [formatScore(summary.rating)])}
				</Text>
			</View>
			<View style={styles.wins}>
				<Text style={[duelTextStyles.gold, { fontSize: 20 * scale }]}>
					{formatScore(summary.wins)}
				</Text>
				<Text style={[duelTextStyles.body, { fontSize: 10 * scale }]}>
					{duelText(t, 'T_SITE_DUELS_WINS')}
				</Text>
			</View>
			{onOpen ? (
				<Pressable accessibilityRole='button' onPress={onOpen} hitSlop={8}>
					{arrow}
				</Pressable>
			) : null}
		</View>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	flex: {
		flex: 1,
		minWidth: 0,
	},
	wins: {
		alignItems: 'center',
	},
});
